/**
 * cgats.ts — Step 0 (parse) + Step 1 (build patches)
 *
 * parseCGATS         — parse raw CGATS text → ParsedCGATS
 * validateMeasuredCGATS — check for Lab columns
 * mergeCGATS         — join target (RGB) + measurement (Lab) by SAMPLE_ID
 * getPatchGroup      — derive PatchGroupId from SAMPLE_ID prefix (throws on unknown)
 * spectralNm         — detect spectral column name → wavelength in nm
 * buildMeasuredPatches — ParsedCGATS → MeasuredPatch[]
 */

import type {
  Lab,
  MeasuredPatch,
  ParsedCGATS,
  PatchGroupId,
  RGB,
  Result,
  SpectralData,
} from '@/types';
import { DEFAULT_PATCH_FLAGS, err, ok } from '@/types';
import { spectralToLab, xyzToLab } from '@/engine/colormath';

// =============================================================================
// Step 0 — Parse raw CGATS text
// =============================================================================

export function parseCGATS(text: string, _filename?: string, keepDuplicates = false): Result<ParsedCGATS> {
  const raw = text;

  // Normalise line endings, strip BOM, trim each line, drop empty + comment lines
  const lines = text
    .replace(/^﻿/, '')     // strip UTF-8 BOM
    .replace(/\r\n?/g, '\n')   // normalise CRLF → LF
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.length > 0 && !l.startsWith('#'));

  if (lines.length === 0) return err('CGATS_PARSE_ERROR: empty file');

  const header: Record<string, string> = {};
  let i = 0;

  // ── 1. Collect header keywords (everything before BEGIN_DATA_FORMAT) ────────
  while (i < lines.length && !lines[i]!.startsWith('BEGIN_DATA_FORMAT')) {
    const line = lines[i]!;
    const m = line.match(/^(\w[\w.]*)\s+"([^"]*)"$/)
           ?? line.match(/^(\w[\w.]*)\s+(.+)$/);
    if (m) header[m[1]!] = m[2]!.trim();
    i++;
  }

  // ── 2. Extract field names ──────────────────────────────────────────────────
  if (i >= lines.length) return err('CGATS_PARSE_ERROR: BEGIN_DATA_FORMAT not found');
  i++; // skip BEGIN_DATA_FORMAT

  // ── 2a. Collect raw DATA_FORMAT content, detect delimiter ──────────────────
  const fmtLines: string[] = [];
  while (i < lines.length && !lines[i]!.startsWith('END_DATA_FORMAT')) {
    fmtLines.push(lines[i]!);
    i++;
  }

  const fmtRaw = fmtLines.join(' ');
  // Barbieri and some other instruments use ';' or ',' to separate fields
  const hasSemicolon = fmtRaw.includes(';');
  const hasComma     = !hasSemicolon && fmtRaw.includes(',');
  const dataDelim    = hasSemicolon ? /[;\s]+/ : hasComma ? /[,\s]+/ : /\s+/;

  const fields: string[] = fmtRaw.split(dataDelim).filter(Boolean);

  if (fields.length === 0) return err('CGATS_PARSE_ERROR: no fields defined');

  const hasSampleId = fields.includes('SAMPLE_ID');

  i++; // skip END_DATA_FORMAT

  // ── 3. Find BEGIN_DATA ──────────────────────────────────────────────────────
  while (i < lines.length && !lines[i]!.startsWith('BEGIN_DATA')) i++;
  if (i >= lines.length) return err('CGATS_PARSE_ERROR: BEGIN_DATA not found');
  i++; // skip BEGIN_DATA

  // ── 4. Parse data rows ──────────────────────────────────────────────────────
  const rows: Record<string, string | number>[] = [];
  const seenIds = new Map<string, number>(); // id → row index
  let syntheticIdx = 0;

  while (i < lines.length && !lines[i]!.startsWith('END_DATA')) {
    const tokens = lines[i]!.split(dataDelim).filter(Boolean);
    i++;

    if (tokens.length === 0) continue;
    if (tokens.length !== fields.length) continue; // skip malformed rows

    const row: Record<string, string | number> = {};
    for (let fi = 0; fi < fields.length; fi++) {
      const token = tokens[fi]!;
      const n = Number(token);
      row[fields[fi]!] = (token.trim() !== '' && !isNaN(n)) ? n : token;
    }

    // When SAMPLE_ID is absent from DATA_FORMAT, synthesize a unique row ID
    if (!hasSampleId) {
      row['SAMPLE_ID'] = 'PATCH_' + String(++syntheticIdx).padStart(5, '0');
    }

    const id = String(row['SAMPLE_ID']!);
    if (!keepDuplicates && seenIds.has(id)) {
      const oldIdx = seenIds.get(id)!;
      rows.splice(oldIdx, 1);
      seenIds.forEach((v, k) => {
        if (v > oldIdx) seenIds.set(k, v - 1);
      });
    }
    if (!keepDuplicates) seenIds.set(id, rows.length);
    rows.push(row);
  }

  if (rows.length === 0) return err('CGATS_PARSE_ERROR: no data rows found');

  // ── 5. Validate NUMBER_OF_SETS ─────────────────────────────────────────────
  const expectedStr = header['NUMBER_OF_SETS'];
  if (expectedStr !== undefined) {
    const expected = Number(expectedStr);
    if (!isNaN(expected) && rows.length !== expected) {
      return err(
        'CGATS_PARSE_ERROR: NUMBER_OF_SETS declares ' + expected +
        ' rows but ' + rows.length + ' were parsed',
      );
    }
  }

  return ok({
    originator:   header['ORIGINATOR'] ?? '',
    descriptor:   header['DESCRIPTOR'] ?? '',
    created:      header['CREATED']    ?? '',
    fields,
    numberOfSets: rows.length,
    rows,
    raw,
  });
}

// =============================================================================
// Validate that a CGATS has RGB + at least one convertible measurement type
// =============================================================================

export function validateMeasuredCGATS(cgats: ParsedCGATS): Result<true> {
  const missingRgb = ['RGB_R', 'RGB_G', 'RGB_B'].filter(f => !cgats.fields.includes(f));
  if (missingRgb.length > 0) {
    return err('MISSING_RGB_DATA: fields missing: ' + missingRgb.join(', '));
  }
  const hasLab      = ['LAB_L', 'LAB_A', 'LAB_B'].every(f => cgats.fields.includes(f));
  const hasXYZ      = ['XYZ_X', 'XYZ_Y', 'XYZ_Z'].every(f => cgats.fields.includes(f));
  const hasSpectral = cgats.fields.some(f => spectralNm(f) !== null);
  if (!hasLab && !hasXYZ && !hasSpectral) {
    return err('MISSING_LAB_DATA: file has no Lab (LAB_L/A/B), XYZ (XYZ_X/Y/Z), or spectral data');
  }
  return ok(true);
}

// =============================================================================
// Convert spectral / XYZ → Lab and enrich ParsedCGATS with LAB_L/A/B columns
// =============================================================================

export function convertMeasurement(cgats: ParsedCGATS): Result<ParsedCGATS> {
  // Already has Lab — nothing to do
  if (['LAB_L', 'LAB_A', 'LAB_B'].every(f => cgats.fields.includes(f))) {
    return ok(cgats);
  }

  const hasXYZ = ['XYZ_X', 'XYZ_Y', 'XYZ_Z'].every(f => cgats.fields.includes(f));
  const spectralFields = cgats.fields
    .map(f => ({ field: f, nm: spectralNm(f) ?? -1 }))
    .filter(x => x.nm >= 0)
    .sort((a, b) => a.nm - b.nm);

  if (!hasXYZ && spectralFields.length < 10) {
    return err('CONVERT_ERROR: insufficient data for Lab conversion (need XYZ or ≥10 spectral bands)');
  }

  const newRows = cgats.rows.map(row => {
    let lab: Lab;
    if (spectralFields.length >= 10) {
      const nm: number[] = [], values: number[] = [];
      for (const { field, nm: wl } of spectralFields) {
        const v = Number(row[field]);
        if (!isNaN(v)) { nm.push(wl); values.push(v); }
      }
      lab = spectralToLab(nm, values);
    } else {
      // XYZ → Lab (CGATS convention: Y=100 for D50 white)
      lab = xyzToLab([Number(row['XYZ_X']), Number(row['XYZ_Y']), Number(row['XYZ_Z'])]);
    }
    return { ...row, LAB_L: lab[0], LAB_A: lab[1], LAB_B: lab[2] };
  });

  const newFields = [...cgats.fields, 'LAB_L', 'LAB_A', 'LAB_B'];
  return ok({ ...cgats, fields: newFields, rows: newRows });
}

// Derive calibration group from rounded RGB values (for files without SAMPLE_ID)
function patchGroupFromRgb(r: number, g: number, b: number): PatchGroupId | null {
  const near = (x: number, y: number) => Math.abs(x - y) < 1;
  const isN  = near(r, g) && near(g, b);
  const isR  = near(g, 255) && near(b, 255) && !isN;
  const isG  = near(r, 255) && near(b, 255) && !isN;
  const isB  = near(r, 255) && near(g, 255) && !isN;
  const isRG = near(r, g)   && near(b, 255) && !isN;
  const isRB = near(r, b)   && near(g, 255) && !isN;
  const isGB = near(g, b)   && near(r, 255) && !isN;
  if (isR)  return 'R';
  if (isG)  return 'G';
  if (isB)  return 'B';
  if (isRG) return 'RG';
  if (isRB) return 'RB';
  if (isGB) return 'GB';
  if (isN)  return 'N';
  return null; // 3D-grid patch — not a calibration ramp
}

// =============================================================================
// Merge target (RGB) + measurement (Lab) CGATS files by SAMPLE_ID
// =============================================================================

export function mergeCGATS(
  target:      ParsedCGATS,
  measurement: ParsedCGATS,
): Result<ParsedCGATS> {
  const measMap = new Map(
    measurement.rows.map(r => [String(r['SAMPLE_ID']!), r]),
  );

  const merged: Record<string, string | number>[] = [];
  for (const tRow of target.rows) {
    const id   = String(tRow['SAMPLE_ID']!);
    const mRow = measMap.get(id);
    if (mRow) merged.push({ ...tRow, ...mRow });
  }

  const matchRate = merged.length / target.rows.length;
  if (matchRate < 0.9) {
    return err(
      'CGATS_MERGE_ERROR: only ' + merged.length + '/' + target.rows.length +
      ' patches matched by SAMPLE_ID',
    );
  }

  const fields = [...new Set([...target.fields, ...measurement.fields])];
  return ok({
    originator:   target.originator,
    descriptor:   target.descriptor,
    created:      target.created,
    fields,
    numberOfSets: merged.length,
    rows:         merged,
    raw:          target.raw,
  });
}

// =============================================================================
// Derive PatchGroupId from SAMPLE_ID prefix — throws for unknown prefixes
// =============================================================================

const NN_LEVELS = new Set([32, 64, 96, 128, 160, 192, 224]);

export function getPatchGroup(sampleId: string): PatchGroupId {
  if (sampleId.startsWith('NN')) {
    const m = sampleId.match(/^NN(\d+)/);
    if (m) {
      const level = Number(m[1]);
      if (NN_LEVELS.has(level)) return ('NN' + level) as PatchGroupId;
    }
    throw new Error('UNKNOWN_GROUP: cannot parse NN level from "' + sampleId + '"');
  }
  if (sampleId.startsWith('RGB')) return 'RGB';  // must precede 'RG', 'RB', 'R'
  if (sampleId.startsWith('RG')) return 'RG';
  if (sampleId.startsWith('RB')) return 'RB';
  if (sampleId.startsWith('GB')) return 'GB';
  if (sampleId.startsWith('R'))  return 'R';
  if (sampleId.startsWith('G'))  return 'G';
  if (sampleId.startsWith('B'))  return 'B';
  if (sampleId.startsWith('N'))  return 'N';
  throw new Error('UNKNOWN_GROUP: unrecognised SAMPLE_ID prefix in "' + sampleId + '"');
}

// =============================================================================
// Spectral field name detection
// =============================================================================

/**
 * Returns the wavelength in nm if field is a spectral reflectance column,
 * or null if it is not a spectral column.
 *
 * Recognised naming conventions:
 *   SPECTRAL_380          — CGATS standard (i1Profiler, ColorThink…)
 *   SPECTRAL_NM_380       — X-Rite/EFI variant (e.g. targ.txt)
 *   R_380 / REFL_380      — some OEM instruments
 *   NM_380                — Barbieri NM_ prefix
 *   380nm / 380.0nm       — Barbieri inline-unit style
 *   nm380                 — prefix style
 *   380                   — bare integer (only accepted in 360–780 nm range)
 */
export function spectralNm(field: string): number | null {
  let m: RegExpMatchArray | null;

  // SPECTRAL_380 | SPECTRAL_NM_380 | R_380 | REFL_380 | NM_380  (case-insensitive prefix)
  m = field.match(/^(?:SPECTRAL(?:_NM)?|R|REFL|NM)_(\d{3,4})$/i);
  if (m) {
    const nm = Number(m[1]);
    return nm >= 300 && nm <= 800 ? nm : null;
  }

  // 380nm | 380.0nm  (Barbieri inline-unit, decimal allowed)
  m = field.match(/^(\d{3,4}(?:\.\d+)?)nm$/i);
  if (m) {
    const nm = Math.round(Number(m[1]));
    return nm >= 300 && nm <= 800 ? nm : null;
  }

  // nm380  (prefix style, no underscore)
  m = field.match(/^nm(\d{3,4})$/i);
  if (m) {
    const nm = Number(m[1]);
    return nm >= 300 && nm <= 800 ? nm : null;
  }

  // Bare integer 360–780 (conservative range to avoid false positives)
  m = field.match(/^(\d{3,4})$/);
  if (m) {
    const nm = Number(m[1]);
    return nm >= 360 && nm <= 780 ? nm : null;
  }

  return null;
}

// =============================================================================
// Step 1 — Build MeasuredPatch[] from ParsedCGATS
// =============================================================================

export function buildMeasuredPatches(
  cgats: ParsedCGATS,
): Result<MeasuredPatch[]> {
  // Validate: must have RGB + at least one measurement type
  const valid = validateMeasuredCGATS(cgats);
  if (!valid.ok) return valid;

  // Auto-convert spectral / XYZ → Lab when Lab columns are absent
  const converted = convertMeasurement(cgats);
  if (!converted.ok) return converted;
  const src = converted.value;

  // Pre-compute spectral field list for archiving original spectral data
  const spectralFields: { field: string; nm: number }[] = cgats.fields
    .map(f => ({ field: f, nm: spectralNm(f) ?? -1 }))
    .filter(x => x.nm >= 0)
    .sort((a, b) => a.nm - b.nm);

  const patches: MeasuredPatch[] = [];
  const errors: string[] = [];

  for (const row of src.rows) {
    const id = String(row['SAMPLE_ID'] ?? '');

    // RGB (integer 0–255)
    const rr = rowNumber(row, 'RGB_R') ?? NaN;
    const gg = rowNumber(row, 'RGB_G') ?? NaN;
    const bb = rowNumber(row, 'RGB_B') ?? NaN;
    if (isNaN(rr) || isNaN(gg) || isNaN(bb)) {
      errors.push('"' + id + '": invalid RGB values');
      continue;
    }
    const rgb: RGB = [
      Math.round(Math.max(0, Math.min(255, rr))),
      Math.round(Math.max(0, Math.min(255, gg))),
      Math.round(Math.max(0, Math.min(255, bb))),
    ];

    // Lab — guaranteed present after convertMeasurement
    const L = rowNumber(row, 'LAB_L') ?? NaN;
    const a = rowNumber(row, 'LAB_A') ?? NaN;
    const b = rowNumber(row, 'LAB_B') ?? NaN;
    if (isNaN(L) || isNaN(a) || isNaN(b)) {
      errors.push('"' + id + '": Lab conversion produced NaN');
      continue;
    }
    const lab: Lab = [L, a, b];

    // Group — fall back to RGB-axis analysis when SAMPLE_ID prefix is unknown
    let group: PatchGroupId | null = null;
    try {
      group = getPatchGroup(id);
    } catch {
      group = patchGroupFromRgb(rgb[0], rgb[1], rgb[2]);
    }
    if (group === null) continue; // 3D-grid patch — not a calibration ramp

    // Archive original spectral reflectance if present
    let spectral: SpectralData | undefined;
    if (spectralFields.length > 0) {
      const nm: number[] = [], values: number[] = [];
      for (const { field, nm: wl } of spectralFields) {
        const v = rowNumber(row, field);
        if (v !== undefined && !isNaN(v)) { nm.push(wl); values.push(v); }
      }
      if (nm.length > 0) spectral = { nm, values };
    }

    const cmy: [number, number, number] = [
      1 - rgb[0] / 255,
      1 - rgb[1] / 255,
      1 - rgb[2] / 255,
    ];

    patches.push({
      id,
      group,
      rgb,
      cmy,
      lab,
      flags: { ...DEFAULT_PATCH_FLAGS },
      ...(spectral ? { spectral } : {}),
    });
  }

  if (errors.length > 0 && patches.length === 0) {
    return err('BUILD_PATCHES_ERROR: all rows failed — ' + errors[0]);
  }
  if (patches.length === 0) {
    return err('BUILD_PATCHES_ERROR: no valid patches produced');
  }

  return ok(patches);
}

// =============================================================================
// Utility: get numeric value from a row, with fallback
// =============================================================================

export function rowNumber(
  row: Record<string, string | number>,
  field: string,
): number | undefined {
  const v = row[field];
  if (v === undefined) return undefined;
  if (typeof v === 'number') return isNaN(v) ? undefined : v;
  const n = Number(v);
  return isNaN(n) ? undefined : n;
}
